import { BACKEND_TYPE_TO_BLOCK, CODE_LANGUAGES, makeBlockId } from "./blocks.data";

// Parses and repairs a pasted content JSON payload into builder blocks.
//
// Authors paste JSON generated outside the app (the KB builder skill, an
// export from another article, a hand-written draft), so the input is
// trusted to be *close* to the contract but never to match it exactly.
// Rather than rejecting a near-miss, this normalises what it can and
// reports every change it made, so nothing is silently rewritten.

const VALID_ALIGN = ["left", "center", "right"];
const VALID_IMAGE_SIZES = ["small", "medium", "large", "full"];
const VALID_NOTE_TYPES = ["info", "tip", "warning", "success"];
const VALID_COLUMNS = [2, 3, 4];

// Legacy / mistaken values seen in hand-written and generated payloads.
const LEGACY_IMAGE_SIZES = { wide: "large", inline: "medium", thumbnail: "small" };
const LEGACY_GALLERY_LAYOUTS = { "grid-2": 2, "grid-3": 3, "grid-4": 4, carousel: 2 };
const PLACEHOLDER_SRC = ["pending_upload", "pending", "tbd", "todo", "none", "null", "#"];

const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const oneOf = (v, list, fallback) => (list.includes(v) ? v : fallback);

const stripHtml = (s) => String(s).replace(/<[^>]+>/g, "");
const hasHtml = (s) => /<[a-zA-Z/][^>]*>/.test(String(s));

// The list/note/heading fields render as text nodes, so any markup an
// author left in them would show up literally on the page.
const toPlainText = (v) =>
  stripHtml(v ?? "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

// Paragraphs are the one rich field. Markdown reaches the renderer as
// literal asterisks, so convert the common subset and wrap bare text.
const toParagraphHtml = (v) => {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  if (hasHtml(raw)) return raw;
  const inline = raw
    .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*\w])\*([^*\n]+)\*(?![*\w])/g, "$1<em>$2</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  return inline
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, " ").trim()}</p>`)
    .join("");
};

// A list item may arrive as a plain string, or as the {text, detail}
// object shape some generators produce. An object reaching the renderer
// throws "Objects are not valid as a React child" and blanks the article,
// so this flattening is what makes those payloads safe to import.
const flattenItem = (item, out, note) => {
  if (typeof item === "string" || typeof item === "number") {
    out.push(toPlainText(item));
    return;
  }
  if (!isObj(item)) return;
  const text = toPlainText(item.text ?? item.label ?? item.title ?? "");
  const detail = toPlainText(item.detail ?? item.description ?? "");
  if (text || detail) {
    out.push([text, detail].filter(Boolean).join(". ").replace(/\.\.+/g, "."));
    note("object list item flattened to a string");
  }
  const subs = item.sub_items ?? item.subItems ?? item.children;
  if (Array.isArray(subs)) {
    subs.forEach((sub) => flattenItem(sub, out, note));
    note("nested list items flattened (the renderer has no nesting)");
  }
};

const normaliseItems = (raw, note) => {
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  list.forEach((item) => flattenItem(item, out, note));
  return out.filter(Boolean);
};

const normaliseImage = (raw, note, fallbackSize) => {
  const src = raw.src ?? raw.url ?? raw.image_url ?? "";
  if (raw.src === undefined && (raw.url !== undefined || raw.image_url !== undefined)) {
    note("image 'url' mapped to 'src'");
  }
  const cleanSrc = PLACEHOLDER_SRC.includes(String(src).trim().toLowerCase()) ? "" : String(src).trim();
  if (cleanSrc === "" && String(src).trim() !== "") {
    note("placeholder image URL replaced with an empty upload slot");
  }
  const size = raw.size ?? raw.width;
  const mapped = LEGACY_IMAGE_SIZES[size] || size;
  if (raw.size === undefined && raw.width !== undefined) note("image 'width' mapped to 'size'");
  return {
    src: cleanSrc,
    alt: toPlainText(raw.alt ?? ""),
    caption: toPlainText(raw.caption ?? ""),
    ...(fallbackSize ? { size: oneOf(mapped, VALID_IMAGE_SIZES, fallbackSize) } : {}),
  };
};

const normaliseBlock = (type, raw, note) => {
  const c = isObj(raw) ? raw : {};

  switch (type) {
    case "heading":
      return {
        text: toPlainText(c.text ?? c.title ?? ""),
        level: oneOf(Number(c.level), [1, 2, 3, 4], 2),
        align: oneOf(c.align, VALID_ALIGN, "left"),
      };

    case "paragraph": {
      const text = c.text ?? c.html ?? c.content ?? "";
      if (text && !hasHtml(text)) note("paragraph text wrapped in <p> tags");
      return { text: toParagraphHtml(text) };
    }

    case "bulletList":
    case "numberedList":
      return { items: normaliseItems(c.items ?? c.list ?? c.steps, note) };

    case "image":
      return {
        ...normaliseImage(c, note, "medium"),
        align: oneOf(c.align, VALID_ALIGN, "center"),
      };

    case "gallery": {
      let columns = Number(c.columns);
      if (!VALID_COLUMNS.includes(columns)) {
        const fromLayout = LEGACY_GALLERY_LAYOUTS[c.layout];
        if (fromLayout) note(`gallery 'layout: ${c.layout}' mapped to columns ${fromLayout}`);
        columns = fromLayout || 2;
      }
      const images = (Array.isArray(c.images) ? c.images : [])
        .filter(isObj)
        .map((img) => normaliseImage(img, note, null));
      return { columns, images: images.length ? images : [{ src: "", alt: "", caption: "" }] };
    }

    case "code": {
      const language = String(c.language ?? "").toLowerCase();
      const safe = CODE_LANGUAGES.includes(language) ? language : "bash";
      if (language && safe !== language) {
        note(`code language '${language}' is not offered by the builder, set to '${safe}'`);
      }
      return {
        language: safe,
        code: String(c.code ?? c.text ?? "").replace(/^```[a-z]*\n?|\n?```$/g, ""),
        showLineNumbers: !!c.showLineNumbers,
      };
    }

    case "note": {
      const incoming = String(c.noteType ?? c.variant ?? c.type ?? "info").toLowerCase();
      if (c.noteType === undefined && c.variant !== undefined) note("note 'variant' mapped to 'noteType'");
      if (incoming === "danger" || incoming === "error") {
        note("note type 'danger' mapped to 'warning' (the only severity the renderer has)");
      }
      const noteType =
        incoming === "danger" || incoming === "error" ? "warning" : oneOf(incoming, VALID_NOTE_TYPES, "info");
      return {
        noteType,
        title: toPlainText(c.title ?? ""),
        text: toPlainText(c.text ?? c.body ?? ""),
      };
    }

    default:
      return {};
  }
};

const resolveType = (raw) => {
  const key = String(raw ?? "").trim();
  if (BACKEND_TYPE_TO_BLOCK[key.toUpperCase()]) return BACKEND_TYPE_TO_BLOCK[key.toUpperCase()];
  const lower = key.toLowerCase().replace(/[\s_-]/g, "");
  const match = Object.values(BACKEND_TYPE_TO_BLOCK).find((t) => t.toLowerCase() === lower);
  return match || null;
};

// Pulls the block array out of whichever envelope the payload arrived in:
// a bare array, the API create body, or a {article, contents} wrapper.
const extractBlocks = (data) => {
  if (Array.isArray(data)) return data;
  if (!isObj(data)) return null;
  for (const key of ["contents", "kb_contents", "blocks", "content"]) {
    if (Array.isArray(data[key])) return data[key];
  }
  if (isObj(data.article) && Array.isArray(data.article.contents)) return data.article.contents;
  return null;
};

const isEmptyBlock = (type, data) => {
  if (type === "bulletList" || type === "numberedList") return data.items.length === 0;
  if (type === "gallery") return false;
  if (type === "image") return false;
  if (type === "code") return !data.code.trim();
  if (type === "note") return !data.text.trim();
  return !stripHtml(data.text || "").trim();
};

export const parseContentJson = (input) => {
  const raw = String(input || "").trim();
  if (!raw) return { blocks: [], repairs: [], error: null };

  let data;
  try {
    data = JSON.parse(raw);
  } catch (e) {
    const at = /position (\d+)/.exec(e.message);
    const line = at ? raw.slice(0, Number(at[1])).split("\n").length : null;
    const reason = e.message.replace(/ in JSON at position \d+.*$/, "").replace(/,\s*"[\s\S]*$/, "");
    return {
      blocks: [],
      repairs: [],
      error: line ? `Not valid JSON — ${reason} (line ${line}).` : `Not valid JSON — ${reason}.`,
    };
  }

  const list = extractBlocks(data);
  if (!list) {
    return {
      blocks: [],
      repairs: [],
      error: 'No content blocks found. Paste an array of blocks, or an object with a "contents" array.',
    };
  }
  if (!list.length) {
    return { blocks: [], repairs: [], error: "The payload has no content blocks." };
  }

  const repairs = [];
  const seen = new Set();
  const note = (msg) => {
    if (!seen.has(msg)) {
      seen.add(msg);
      repairs.push(msg);
    }
  };

  // display_order is assigned by the API from array position, but a payload
  // exported from the DB carries it — honour it when every block has one.
  const ordered = list.every((b) => isObj(b) && Number.isInteger(b.display_order))
    ? [...list].sort((a, b) => a.display_order - b.display_order)
    : list;

  const blocks = [];
  let skipped = 0;

  ordered.forEach((entry) => {
    if (!isObj(entry)) {
      skipped += 1;
      return;
    }
    const type = resolveType(entry.type);
    if (!type) {
      skipped += 1;
      return;
    }
    const data = normaliseBlock(type, entry.content ?? entry.data ?? entry, note);
    if (isEmptyBlock(type, data)) {
      skipped += 1;
      return;
    }
    blocks.push({ id: makeBlockId(), type, data });
  });

  if (skipped) note(`${skipped} block${skipped > 1 ? "s" : ""} skipped as empty or an unknown type`);

  if (!blocks.length) {
    return {
      blocks: [],
      repairs,
      error: "None of the blocks could be read. Check that each one has a valid \"type\" and \"content\".",
    };
  }

  return { blocks, repairs, error: null };
};

// The reference payload offered as a download when an import fails.
export const CONTENT_FORMAT_SAMPLE = {
  contents: [
    { type: "NOTE", content: { noteType: "info", title: "In this article", text: "What the reader will be able to do." } },
    { type: "PARAGRAPH", content: { text: "<p>Body copy. HTML only — <strong>bold</strong> like this, never **asterisks**.</p>" } },
    { type: "HEADING", content: { text: "Section title", level: 2, align: "left" } },
    { type: "BULLET_LIST", content: { items: ["Plain strings only", "Never objects like {\"text\": \"...\"}"] } },
    { type: "NUMBERED_LIST", content: { items: ["Imperative verb first.", "Carry the why in a second sentence."] } },
    { type: "IMAGE", content: { src: "", alt: "Describes the image for screen readers", caption: "Adds what the image does not show", size: "full", align: "center" } },
    { type: "GALLERY", content: { columns: 3, images: [{ src: "", alt: "First", caption: "First" }, { src: "", alt: "Second", caption: "Second" }] } },
    { type: "CODE_BLOCK", content: { language: "json", code: "{\n  \"key\": \"value\"\n}", showLineNumbers: false } },
    { type: "NOTE", content: { noteType: "warning", title: "The one thing people get wrong", text: "info, tip, warning or success. There is no danger type." } },
  ],
};
